import React, { useState, useEffect, useRef} from "react";

import {
  useNavigate,
  useLocation,
} from "react-router-dom";

import {
  auth,
  googleProvider,
  signInWithPopup,
  firestore,
  query,
  where,
  getDocs,
  collection,
} from "./firebase";
import ReCaptcha from "./WarrantyComponents/Auth/ReCaptcha";
import { handleUserProfile } from './WarrantyComponents/DatabaseAPI/Api';
import { generateOtp, isOtpValid } from './otp/OtpService';
import "./Login.css";

/* ───────── constants ───────── */
const TICKER_ITEMS = [
  "MEMBERSHIP ADMIN",
  "FACILITY MANAGEMENT",
  "SMART ALERTS",
  "LIVE DASHBOARD",
  "ROLE-BASED ACCESS",
  "WARRANTY TRACKING",
  "AMC MANAGEMENT",
  "CONTRACT MANAGEMENT",
];

const FEATURES = [
  { icon: "🛡️", title: "Warranty Tracking", desc: "Auto-track expiry dates. Get reminders at 90, 60, and 30 days. Never lose a claim due to missed paperwork.", tag: "COVERAGE", num: "01" },
  { icon: "🔧", title: "AMC Schedules", desc: "Schedule service visits, assign engineers, and track completion. Full visibility across all facilities.", tag: "MAINTENANCE", num: "02" },
  { icon: "📄", title: "Contract Management", desc: "Upload vendor agreements. Track obligations and renewal windows. Negotiate from a position of strength.", tag: "LEGAL", num: "03" },
  { icon: "👥", title: "Membership Module", desc: "Approve memberships, track renewals, and manage your member base without drowning in emails.", tag: "MEMBERS", num: "04" },
  { icon: "🏢", title: "Facility Management", desc: "Multi-site? No problem. Assign admins per location and keep your entire portfolio organised.", tag: "FACILITIES", num: "05" },
  { icon: "🔔", title: "Smart Notifications", desc: "Configurable email and in-app alerts. Your team acts before deadlines hit – not after.", tag: "ALERTS", num: "06" },
];

const HOW_STEPS = [
  { num: "01", icon: "🔐", title: "Sign in — no password needed", desc: "Use Google or receive a one-time email code. Your account is live in seconds, with no setup friction." },
  { num: "02", icon: "📋", title: "Add your assets and contracts", desc: "Import from spreadsheets or add manually. Create warranty cards, AMC schedules, and vendor contracts in minutes." },
  { num: "03", icon: "⚡", title: "Stay ahead of every deadline", desc: "Automated alerts ensure your team acts before warranties lapse, AMC dates pass, or contracts expire unchecked." },
];

const FAQ_DATA = [
  { q: "Is Aalay really free to start?", a: "Yes. Our Starter plan is free forever for up to 3 users with 50 warranty records. No credit card required. You only pay when you need more scale." },
  { q: "How does the OTP login work?", a: "Enter your email and we'll send a one-time code. Verify it, and you're in — no password to remember, no friction." },
  { q: "Can I manage multiple locations?", a: "Absolutely. Create separate facilities, assign local admins, and maintain full visibility from a single dashboard." },
  { q: "What kind of alerts does Aalay send?", a: "Email and in-app notifications for warranty expiry, AMC due dates, contract renewals, and membership lapses. All configurable per user." },
];

const OTP_EXPIRY_MINUTES = 1;

/* ───────── component ───────── */
const Login = ({ onLoginSuccess }) => {
  const navigate = useNavigate();

  const location = useLocation();
  const returnUrl = new URLSearchParams(location.search).get("returnUrl");


  const [isVerified, setIsVerified] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);

  /* OTP state (inline, no separate component) */
  const [email, setEmail] = useState("");
  const [generatedOtp, setGeneratedOtp] = useState("");
  const [generatedAt, setGeneratedAt] = useState(null);
  const [inputOtp, setInputOtp] = useState("");
  const [otpStatus, setOtpStatus] = useState("");
  const [otpSent, setOtpSent] = useState(false);

  /* FAQ accordion */
  const [openFaq, setOpenFaq] = useState(0);

  /* refs for smooth scroll */
  const featuresRef = useRef(null);
  const howRef = useRef(null);
  const faqRef = useRef(null);
  const loginRef = useRef(null);


  const findUserProfileByEmail = async (email) => {
    const normalizedEmail = String(email || "").trim().toLowerCase();
    if (!normalizedEmail) return null;

    const profileById = new Map();
    const emailValues = Array.from(
      new Set([normalizedEmail, String(email || "").trim()].filter(Boolean))
    );

    await Promise.all(
      emailValues.map(async (emailValue) => {
        const roleQuery = query(
          collection(firestore, "UserProfileMaster"),
          where("email", "==", emailValue)
        );
        const roleSnapshot = await getDocs(roleQuery);
        roleSnapshot.docs.forEach((profileDoc) => {
          profileById.set(profileDoc.id, { id: profileDoc.id, ...profileDoc.data() });
        });
      })
    );

    const profiles = Array.from(profileById.values());
    if (!profiles.length) return null;

    return (
      profiles.find(
        (profile) => String(profile.role || "").trim().toLowerCase() === "technician"
      ) || profiles[0]
    );
  };

  const saveLoginSession = ({ email, role, userProfile }) => {
    const tenantId = userProfile?.tenantId || userProfile?.tenantid || null;

    sessionStorage.setItem("userRole", role);
    sessionStorage.setItem("userEmail", email);

    if (tenantId) {
      sessionStorage.setItem("tenantId", tenantId);
    } else {
      sessionStorage.removeItem("tenantId");
    }

    if (userProfile) {
      sessionStorage.setItem("userProfile", JSON.stringify(userProfile));
      sessionStorage.setItem(
        "userDisplayName",
        userProfile.displayName || userProfile.adminName || ""
      );
    } else {
      sessionStorage.removeItem("userProfile");
      sessionStorage.removeItem("userDisplayName");
    }

    return tenantId;
  };


  useEffect(() => { if (isVerified) setErrorMessage(""); }, [isVerified]);


  /* ── auth helpers (unchanged logic) ── */
  const showError = (msg) => setErrorMessage(msg);

  const authenticateUser = async (user) => {


  try {

    const superadminEmails = [

      process.env.REACT_APP_SUPERADMIN_EMAIL1,

      process.env.REACT_APP_SUPERADMIN_EMAIL2,

      process.env.REACT_APP_SUPERADMIN_EMAIL3,

      process.env.REACT_APP_SUPERADMIN_EMAIL4,

      process.env.REACT_APP_SUPERADMIN_EMAIL5,

    ].filter(Boolean);



    let role = "customer";
    let userProfile = null;



    if (superadminEmails.includes(user.email)) {

      role = "superadmin";

    } else {

      userProfile = await findUserProfileByEmail(user.email);

      if (userProfile) {
        role = userProfile.role || "customer";
      }

    }



    console.log(`Logged in as role: ${role}`);

    const tenantId = saveLoginSession({ email: user.email, role, userProfile });



    await handleUserProfile(user, null);



    // Call the onLoginSuccess prop from App.js

    if (onLoginSuccess) {

      onLoginSuccess({ email: user.email, role, tenantId, returnUrl });

    } else {

      // Fallback if no prop passed

      navigate("/");

    }



  } catch (error) {

    console.error("Authentication Error:", error);

    showError("Something went wrong during authentication.");

  }

};



  const handleOtpSuccess = async (email) => {

    try {
      const superadminEmailsForSession = [
        process.env.REACT_APP_SUPERADMIN_EMAIL1,
        process.env.REACT_APP_SUPERADMIN_EMAIL2,
        process.env.REACT_APP_SUPERADMIN_EMAIL3,
        process.env.REACT_APP_SUPERADMIN_EMAIL4,
        process.env.REACT_APP_SUPERADMIN_EMAIL5,
      ].filter(Boolean);
      const resolvedProfile = await findUserProfileByEmail(email);
      const resolvedRole = superadminEmailsForSession.includes(email)
        ? "superadmin"
        : resolvedProfile?.role || "customer";
      const resolvedTenantId = saveLoginSession({
        email,
        role: resolvedRole,
        userProfile: resolvedProfile,
      });

      // First check if there's already a Firebase user logged in

      const currentUser = auth.currentUser;

      

      if (
        currentUser &&
        String(currentUser.email || "").trim().toLowerCase() ===
          String(email || "").trim().toLowerCase()
      ) {

        // User is already logged in via Firebase, use existing session

        console.log(`Using existing Firebase session for: ${email}`);

        

        // Refresh the role from Firestore so stale seller/shop sessions do not override Technician.

        

        // Call onLoginSuccess prop with existing user data

        if (onLoginSuccess) {

          onLoginSuccess({ 

            email: currentUser.email, 

            role: resolvedRole, 

            tenantId: resolvedTenantId, 

            returnUrl,

          });

        } else {

          navigate("/");

        }

        return;

      }



      // Check if there's already a session for this email (Google or OTP)

      const storedEmail = sessionStorage.getItem("userEmail");

      const storedRole = sessionStorage.getItem("userRole");

      

      if (storedEmail === email && storedRole) {

        // Refresh session data from Firestore before continuing.

        console.log(`Using existing session data for: ${email}`);

        

        // Set OTP flag to indicate this was an OTP login

        sessionStorage.setItem("otpLoggedIn", "true");

        

        if (onLoginSuccess) {

          onLoginSuccess({ 

            email: storedEmail, 

            role: resolvedRole, 

            tenantId: resolvedTenantId,

            returnUrl,

          });

        } else {

          navigate("/");

        }

        return;

      }



      // If no existing session, proceed with OTP authentication

      // Query Firestore to get user role based on email


      const superadminEmails = [
        process.env.REACT_APP_SUPERADMIN_EMAIL1,
        process.env.REACT_APP_SUPERADMIN_EMAIL2,
        process.env.REACT_APP_SUPERADMIN_EMAIL3,
        process.env.REACT_APP_SUPERADMIN_EMAIL4,
        process.env.REACT_APP_SUPERADMIN_EMAIL5,
      ].filter(Boolean);

      let role = "customer";

      let userProfile = null;



      if (superadminEmails.includes(email)) {

        role = "superadmin";
      } else {


        userProfile = await findUserProfileByEmail(email);

        if (userProfile) {
          role = userProfile.role || "customer";
        }

      }



      console.log(`OTP Logged in as role: ${role}`);



      // Set sessionStorage for OTP user

      const tenantId = saveLoginSession({ email, role, userProfile });

      sessionStorage.setItem("otpLoggedIn", "true");



      // Call onLoginSuccess prop from App.js

      if (onLoginSuccess) {

        onLoginSuccess({ email, role, tenantId, returnUrl });


      } else {
        navigate("/");
      }
    } catch (err) {
      console.error("Authentication Error:", err);
      showError("Something went wrong during authentication.");
    }
  };

  const SignInWithProvider = async (provider) => {
    if (!isVerified) { showError("Please complete the ReCaptcha verification first."); return; }
    setLoading(true);
    setErrorMessage("");
    try {
      const result = await signInWithPopup(auth, provider);
      await authenticateUser(result.user);
    } catch (err) {
      console.error("Login Error:", err);
      showError(err.code === "auth/popup-closed-by-user" ? "Login cancelled. Please try again." : "Login failed. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  /* ── OTP helpers ── */
  const sendOtp = async () => {
    if (!isVerified) { setOtpStatus("Please complete the ReCaptcha verification first."); return; }
    if (!email.trim()) { setOtpStatus("Please enter your email first."); return; }

    const otp = generateOtp();
    setGeneratedOtp(otp);
    setGeneratedAt(Date.now());

    const jsonPayload = {
      email,
      subject: 'Your OTP Code',
      textbody: `Your OTP is ${otp}. This code will expire in ${OTP_EXPIRY_MINUTES} minutes.`,
      htmlbody: `<div style="font-family:Arial,sans-serif;color:#333;"><h2>Your OTP Code</h2><p>Your OTP is <strong>${otp}</strong>.</p><p>This code will expire in ${OTP_EXPIRY_MINUTES} minutes.</p></div>`,
      apiKey: 'qwertyuiop',
    };

    const formData = new FormData();
    formData.append('jsonPayload', JSON.stringify(jsonPayload));

    try {
      const response = await fetch('https://email-a306.onrender.com/send-email', { method: 'POST', body: formData });
      if (response.ok) { setOtpStatus("OTP sent to your email."); setOtpSent(true); }
      else { setOtpStatus("Failed to send OTP. Please try again."); }
    } catch { setOtpStatus("Error sending OTP. Please try again."); }
  };

  const validateOtp = () => {
    const verifiedEmail = email.trim();
    if (!verifiedEmail) { setOtpStatus("Email not found. Please re-enter."); return; }

    if (inputOtp === generatedOtp && isOtpValid(generatedAt, OTP_EXPIRY_MINUTES)) {
      setOtpStatus("OTP verified! Redirecting...");
      setTimeout(() => handleOtpSuccess(verifiedEmail), 1000);
    } else {
      setOtpStatus("Invalid or expired OTP. Please try again.");
    }
  };

 

  const scrollTo = (ref) => ref.current?.scrollIntoView({ behavior: "smooth" });

  /* ───────── render ───────── */
  return (
    <div className="maintain-landing">

      {/* ════════ NAVBAR ════════ */}
      <nav className="ml-navbar">
        <a className="ml-navbar-logo" href="#top">
          <span className="ml-navbar-brand">
            AALAY<span style={{ color: '#f59e0b', fontSize: '1.4rem' }}>.</span>
          </span>
          <span className="ml-navbar-subtitle">Intelligent Operations by OmneVu.com</span>
        </a>
        <ul className="ml-nav-links">
          <li><a href="#features" onClick={(e) => { e.preventDefault(); scrollTo(featuresRef); }}>Features</a></li>
          <li><a href="#how" onClick={(e) => { e.preventDefault(); scrollTo(howRef); }}>How it works</a></li>
          <li><a href="#faq" onClick={(e) => { e.preventDefault(); scrollTo(faqRef); }}>FAQ</a></li>
        </ul>
        <div className="ml-nav-actions">
          <button className="ml-btn-ghost" onClick={() => scrollTo(loginRef)}>Sign in</button>
          <button className="ml-btn-primary" onClick={() => navigate("/newusersignin")}>GET STARTED →</button>
        </div>
      </nav>

      {/* ════════ HERO ════════ */}
      <section className="ml-hero" id="top">
        <div className="ml-hero-content">
          <span className="ml-kicker"><span className="ml-kicker-dot"></span> WARRANTY & ASSET MANAGEMENT</span>
          <h1>Manage<br />Every Asset.<br /><span className="gold">Effortlessly.</span></h1>
          <p className="ml-hero-sub">
            Aalay tracks warranties, AMC schedules, contracts and
            memberships — one intelligent platform for modern
            organisations.
          </p>
          <div className="ml-hero-cta">
            <button className="ml-btn-hero" onClick={() => scrollTo(loginRef)}>GET STARTED FOR FREE</button>
          </div>
        </div>

        {/* decorative shapes */}
        <div className="ml-circle ml-circle-1"></div>
        <div className="ml-circle ml-circle-2"></div>
        <div className="ml-circle ml-circle-3"></div>
      </section>

      {/* ════════ TICKER ════════ */}
      <div className="ml-ticker">
        <div className="ml-ticker-track">
          {[...TICKER_ITEMS, ...TICKER_ITEMS].map((t, i) => (
            <span className="ml-ticker-item" key={i}>{t}</span>
          ))}
        </div>
      </div>

      {/* ════════ FEATURES ════════ */}
      <section className="ml-features" ref={featuresRef}>
        <span className="ml-section-kicker">WHAT WE DO</span>
        <h2>Everything you need,<br /><span className="gold">in one place.</span></h2>
        <p className="ml-features-sub">
          Powerful modules for modern operations, compliance teams, and growing businesses.
        </p>
        <div className="ml-features-grid">
          {FEATURES.map((f, i) => (
            <div className="ml-feature-card" key={i}>
              <div className="ml-feature-icon">{f.icon}</div>
              <h3>{f.title}</h3>
              <p>{f.desc}</p>
              <span className="ml-feature-tag">{f.tag}</span>
              <span className="ml-feature-number">{f.num}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ════════ HOW IT WORKS ════════ */}
      <section className="ml-how" ref={howRef}>
        <span className="ml-section-kicker">HOW IT WORKS</span>
        <h2>Up and running<br />in three steps.</h2>
        <p className="ml-how-sub">
          No training needed. No IT department required. Just sign in
          and go.
        </p>
        <div className="ml-how-grid">
          {HOW_STEPS.map((s, i) => (
            <div className="ml-how-step" key={i}>
              <div className="ml-how-step-num">{s.num}</div>
              <div className="ml-how-step-icon">{s.icon}</div>
              <h3>{s.title}</h3>
              <p>{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ════════ FAQ ════════ */}
      <section className="ml-faq" ref={faqRef}>
        <span className="ml-section-kicker">FAQ</span>
        <h2>Common <span className="gold">questions.</span></h2>
        <p className="ml-faq-sub">Quick answers to what teams ask us most.</p>
        {FAQ_DATA.map((item, i) => (
          <div className={`ml-faq-item${openFaq === i ? " open" : ""}`} key={i} onClick={() => setOpenFaq(openFaq === i ? -1 : i)}>
            <div className="ml-faq-q">
              <span>{item.q}</span>
              <span className="ml-faq-toggle">+</span>
            </div>
            <div className="ml-faq-a">{item.a}</div>
          </div>
        ))}
      </section>

      {/* ════════ CTA BANNER ════════ */}
      <section className="ml-cta-banner">
        <h2>Ready to take control?</h2>
        <p>Join hundreds of teams that never miss a warranty or deadline again.</p>
        <button className="ml-btn-hero" onClick={() => scrollTo(loginRef)}>GET STARTED FREE</button>
      </section>

      {/* ════════ LOGIN SECTION ════════ */}
      <section className="ml-login-section" ref={loginRef} id="login">
        {/* left */}
        <div className="ml-login-left">
          <h2>SIGN IN<br />TO YOUR<br />WORKSPACE.</h2>
          <p className="ml-login-left-sub">Your assets, contracts, and maintenance schedules are one secure step away.</p>

          <div className="ml-stat">
            <div><div className="ml-stat-label">TOTAL WARRANTIES</div><div className="ml-stat-sub">Tracked right now</div></div>
            <div className="ml-stat-value">1,247</div>
          </div>
          <div className="ml-stat">
            <div><div className="ml-stat-label">AMC SCHEDULES</div><div className="ml-stat-sub">Active contracts</div></div>
            <div className="ml-stat-value">83</div>
          </div>
          <div className="ml-stat">
            <div><div className="ml-stat-label">PLATFORM UPTIME</div><div className="ml-stat-sub">Last 12 months</div></div>
            <div className="ml-stat-value">99.9%</div>
          </div>

          <div className="ml-circle" style={{ width: 200, height: 200, bottom: -40, right: -40 }}></div>
        </div>

        {/* right */}
        <div className="ml-login-right">
          <h2>WELCOME BACK.</h2>
          <p className="ml-login-right-sub">Enter your details below to access your dashboard.</p>

          {errorMessage && <div className="ml-error-alert">{errorMessage}</div>}

          <div className="ml-recaptcha-wrap">
            <ReCaptcha onVerify={(v) => setIsVerified(v)} />
          </div>

          {/* Google button */}
          <button className="ml-google-btn" onClick={() => SignInWithProvider(googleProvider)} disabled={loading}>
            {loading ? (
              <span className="ml-spinner"></span>
            ) : (
              <>
                <svg viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A11.96 11.96 0 0 0 0 12c0 1.94.46 3.77 1.28 5.41l3.56-2.77.01-.55z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                Continue with Google
              </>
            )}
          </button>

          <div className="ml-divider">or with email code</div>

          {/* OTP inline form */}
          <div className="ml-otp-box">
            <h3>PASSWORDLESS LOGIN</h3>
            <label className="ml-otp-label">Your email address</label>
            <input
              className="ml-otp-input"
              type="email"
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <button className="ml-send-btn" onClick={sendOtp} disabled={loading}>
              SEND LOGIN CODE
            </button>

            {otpSent && (
              <>
                <input
                  className="ml-verify-input"
                  type="text"
                  placeholder="Enter OTP"
                  value={inputOtp}
                  onChange={(e) => setInputOtp(e.target.value)}
                />
                <button className="ml-verify-btn" onClick={validateOtp}>
                  VERIFY OTP
                </button>
              </>
            )}

            {otpStatus && (
              <div className={`ml-otp-status ${otpStatus.startsWith("OTP verified") ? "success" : "error"}`}>
                {otpStatus}
              </div>
            )}
          </div>

          <div className="ml-register-link">
            New here? <span onClick={() => navigate("/newusersignin")}>Create your free account</span>
          </div>
        </div>
      </section>

      {/* ════════ FOOTER ════════ */}
      <footer className="ml-footer">
        <span>© 2025 Aalay Powered by omnevu.com</span>
        <span>By using this site you agree to our <a href="/terms-and-conditions" onClick={(e) => { e.preventDefault(); navigate("/terms-and-conditions"); }}>Terms</a> & <a href="/privacy-policy" onClick={(e) => { e.preventDefault(); navigate("/privacy-policy"); }}>Privacy</a></span>
      </footer>
    </div>
  );
};


export default Login;

